import { useState } from "react";
import { api } from "../../lib/api";
import { downscaleToJpeg } from "../../lib/image";
import { hashFor } from "../../lib/routes";

type Shot = { b64: string; w: number; h: number; bytes: number; preview: string };

const CAP = 1600;
const box = "bg-zinc-900 border border-zinc-800 rounded-md p-3 space-y-2";

export default function CardCapture() {
  const [front, setFront] = useState<Shot | null>(null);
  const [back, setBack] = useState<Shot | null>(null);
  const [extras, setExtras] = useState<Shot[]>([]);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function take(which: "front" | "back" | "extra", file: File | undefined): Promise<void> {
    if (!file) return;
    setError(null);
    try {
      const { b64, w, h, bytes } = await downscaleToJpeg(file, CAP);
      const shot: Shot = { b64, w, h, bytes, preview: `data:image/jpeg;base64,${b64}` };
      if (which === "front") setFront(shot);
      else if (which === "back") setBack(shot);
      else setExtras((xs) => [...xs, shot]);
    } catch (e) {
      setError(`${which === "extra" ? "That extra photo" : `The ${which} photo`} could not be used: ${String((e as Error).message)}`);
    }
  }

  async function start(): Promise<void> {
    if (!front || !back || busy) return;
    setBusy(true); setError(null);
    try {
      // The response always says dup_check "deferred" with existing null: a graded card has no
      // cert until the vision read has run, so there is nothing to search for yet (spec §11).
      // The real duplicate answer lands on the session at the end of the CERT stage, and the
      // review page shows it — this page must not pretend to know it here.
      const r = await api.createCardListerSession({ front_b64: front.b64, back_b64: back.b64, extras_b64: extras.map((x) => x.b64) });
      await api.startListerRead(r.id);
      window.location.hash = hashFor.review(r.id);
    } catch (e) {
      setError(String(e));
    } finally {
      setBusy(false);
    }
  }

  const shot = (label: string, which: "front" | "back", value: Shot | null) => (
    <div className={box}>
      <label className="block">
        <span className="block text-xs text-zinc-400 mb-1">{label}</span>
        <input aria-label={label} type="file" accept="image/*" capture="environment" disabled={busy}
          onChange={(e) => { const input = e.target; void take(which, input.files?.[0]).finally(() => { input.value = ""; }); }} />
      </label>
      {value
        ? <><img src={value.preview} alt={`${which} of the card`} className="w-full max-h-64 object-contain rounded bg-zinc-950" />
            <p className="text-xs text-zinc-500">{value.w}×{value.h} · {Math.round(value.bytes / 1024)} KB</p></>
        : <p className="text-xs text-zinc-500">Fill the frame with the card. {CAP}px is plenty — the photo is resized before upload.</p>}
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto px-4 py-4 space-y-4 font-mono text-sm">
      <div className="flex items-center justify-between">
        <h1 className="text-zinc-100 text-base">Lister · card</h1>
        <a href={hashFor.lister()} className="text-xs text-zinc-400 underline">scan a barcode instead</a>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {shot("Front of the card", "front", front)}
        {shot("Back of the card", "back", back)}
      </div>
      <div className={box}>
        <label className="block">
          <span className="block text-xs text-zinc-400 mb-1">Extra photos (optional)</span>
          <input aria-label="Extra photos (optional)" type="file" accept="image/*" capture="environment" disabled={busy}
            onChange={(e) => { const input = e.target; void take("extra", input.files?.[0]).finally(() => { input.value = ""; }); }} />
        </label>
        {extras.length > 0 && <div className="flex gap-2 flex-wrap">{extras.map((x, i) => <img key={i} src={x.preview} alt={`extra ${i + 1}`} className="h-20 rounded bg-zinc-950" />)}</div>}
      </div>
      <button onClick={() => void start()} disabled={!front || !back || busy}
        className="w-full bg-zinc-800 hover:bg-zinc-700 disabled:opacity-40 rounded px-3 py-3 text-zinc-100">
        {busy ? "working…" : "Identify this card"}
      </button>
      {error && <p className="text-red-400" role="alert">{error}</p>}
    </div>
  );
}
